// Payload types for the session-lifecycle names declared in
// `../contributions/analytics.ts`: `session_created`, `session_updated`,
// `session_deleted`, `transcript_deleted`, `personalization_updated` and
// `session_marked_as_context_only`. Extracted from
// `src/models/analytics/features/sessions.ts` (a partial extraction, so no
// rename to follow — history: `git log --follow` that file).
import { type SessionStatus } from '@heidi/schema/ml-scribe';

export enum SessionCreatedEntryPoint {
  Sidebar = 'sidebar',
  CreateButton = 'create_button',
  EmptyState = 'empty_state',
  Integration = 'integration',
  Schedule = 'schedule',
  Deeplink = 'deeplink',
  PatientList = 'patient_list',
  // A session started from the patient surface (profile drawer / full page).
  // Threaded with `view_id` so the open -> session funnel can stitch per-view.
  PatientSurface = 'patient_surface',
  WorkHome = 'work_home',
  Unknown = 'unknown',
}

export type SessionCreatedEventProps = {
  session_id: string;
  entry_point: SessionCreatedEntryPoint;
  is_for_other_user: boolean;
  /**
   * UUID of the patient-profile view this session was started from (2-0-4
   * addition). Stitches the surface open -> session_created funnel. Omitted for
   * sessions not started from the patient surface.
   */
  view_id?: string;
};

/**
 * `session_updated` is ONE name over five distinct edits, discriminated here
 * rather than by five event names — a dashboard splits on `update_action`.
 */
export type SessionUpdatedAction =
  | 'session_name_updated'
  | 'date_time_updated'
  | 'mic_input_changed'
  | 'output_source_changed'
  | 'session_status_updated';

/**
 * What kind of speaker/output source was selected on desktop — a physical
 * device, an app (Windows process-loopback entry), the OS default
 * (`__SYSTEM_DEFAULT__`) or whole-system capture (`__SYSTEM_WIDE__`). The
 * Snowplow schema types the field as a free string, so a value needs no bump.
 */
export type OutputSourceType = 'device' | 'app' | 'system_default' | 'system_wide';

export type SessionUpdatedEventProps = {
  session_id: string;
  update_action: SessionUpdatedAction;
  word_count?: number;
  microphone?: string;
  created_time_local?: string;
  /** For `output_source_changed`: device name, app display name, or 'system_default'. */
  output_source?: string;
  /** For `output_source_changed`: what kind of source was selected. */
  output_source_type?: OutputSourceType;
  /** New value after the update. For `session_status_updated`, the new `SessionStatus`. */
  value?: SessionStatus | string;
};

/**
 * `session_deleted` is ONE name over the two bulk deletions, discriminated by
 * `delete_action` — a single-session delete reports `batch_deleted` with a
 * count of one.
 */
export type SessionDeletedAction = 'batch_deleted' | 'all_deleted';

export type SessionDeletedEventProps = {
  delete_action: SessionDeletedAction;
  session_count?: number;
};

export type TranscriptDeletedEventProps = {
  session_id: string;
};

/**
 * `personalization_updated` covers the opt-in pair and a voice-style edit, so
 * the funnel splits on `update_action` rather than on three names.
 */
export type PersonalizationUpdatedAction =
  | 'custom_voice_updated'
  | 'personalization_turned_on'
  | 'personalization_turned_off';

export type PersonalizationUpdatedEventProps = {
  session_id?: string;
  update_action: PersonalizationUpdatedAction;
  voice_style?: string;
  old_voice_style?: string;
  custom_style?: string;
  old_custom_style?: string;
  transition_type?: string;
  source?: string;
};

export type SessionMarkedAsContextOnlyEventProps = {
  session_id?: string;
};

/**
 * What `session_started_with_document_queue` carries. TRANSCRIBED, not moved:
 * `queue_size` and `template_ids` are the document queue's vocabulary, not
 * session's, so the app union's member stays declared in
 * `src/models/analytics/features/documents.ts` with every importer it has, and
 * the two are held together by a mutual-assignability check in
 * `src/models/analytics/features/session-analytics-shims.test.ts` — the same
 * mechanism as `SessionsFilterPreset`, because both queue fields are required
 * on the app member and a narrowed shape is not assignable to it.
 */
export type SessionStartedWithDocumentQueueAnalyticsProps = {
  session_id: string;
  queue_size: number;
  template_ids: string[];
};
